import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBooking } from '../../hooks/useBooking';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AuthService from '../../services/AuthService';
import { PAGES } from '../../utils/constants';

const ProfilePage = () => {
  const { state: authState, logout } = useAuth();
  const { setCurrentPage } = useBooking();
  const { user, token, isAuthenticated } = authState;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setCurrentPage(PAGES.HOME);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await AuthService.getProfile(token);
        console.log('🎯 DEBUG: Profile API Response:', profileData);
        console.log('🎯 DEBUG: User data:', profileData.user);
        console.log('🎯 DEBUG: Statistics:', profileData.statistics);
        setProfile(profileData);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setError('Failed to load profile data');
        if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
          logout(); // Token expired
          setCurrentPage(PAGES.HOME);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated, token, setCurrentPage, logout]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner message="Loading profile..." size="large" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => setCurrentPage(PAGES.HOME)}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
            >
              Back to Home
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // ✅ FIXED: Access nested user data correctly
  const userData = profile?.user || user; // Use profile.user or fallback to auth user
  const statistics = profile?.statistics || { totalBookings: 0, totalSpent: '0', currency: 'USD', loyaltyPoints: 0 };
  const preferences = profile?.preferences || {};

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center text-gray-900 text-xl font-bold">
                  {userData?.firstName ? userData.firstName[0].toUpperCase() : 'U'}
                  {userData?.lastName ? userData.lastName[0].toUpperCase() : ''}
                </div>
                <div className="ml-4">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {userData?.firstName} {userData?.lastName}
                  </h1>
                  <p className="text-gray-600">{userData?.email}</p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage(PAGES.PROFILE_SETTINGS)}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
              >
                Edit Profile
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {statistics.totalBookings || 0}
                </div>
                <div className="text-gray-600">Total Bookings</div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {statistics.currency || 'USD'} {statistics.totalSpent || '0'}
                </div>
                <div className="text-gray-600">Total Spent</div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {statistics.loyaltyPoints || 0}
                </div>
                <div className="text-gray-600">Loyalty Points</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={() => setCurrentPage(PAGES.HOME)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 p-3 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">New Booking</div>
                  <div className="text-sm text-gray-600">Search and book a new transfer</div>
                </button>
                <button
                  onClick={() => setCurrentPage(PAGES.BOOKING_HISTORY)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 p-3 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Booking History</div>
                  <div className="text-sm text-gray-600">View all your past bookings</div>
                </button>
                <button
                  onClick={() => setCurrentPage(PAGES.PROFILE_SETTINGS)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 p-3 rounded-lg transition-colors"
                >
                  <div className="font-medium text-gray-900">Account Settings</div>
                  <div className="text-sm text-gray-600">Update your profile and preferences</div>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Bookings</h2>
              <div className="text-gray-600 text-center py-8">
                <p>No recent bookings</p>
                <button
                  onClick={() => setCurrentPage(PAGES.HOME)}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                >
                  Book your first ride
                </button>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="text-gray-900">{userData?.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <p className="text-gray-900">{userData?.phone || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Member Since</label>
                <p className="text-gray-900">
                  {userData?.createdAt 
                    ? new Date(userData.createdAt).toLocaleDateString()
                    : 'Unknown'
                  }
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Default Currency</label>
                <p className="text-gray-900">{userData?.defaultCurrency || 'USD'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;